import { FC, useState, useRef, ChangeEvent, DragEvent } from 'react';
import { Box, VStack, HStack, Text, Button } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { documentService } from '@/services/firebase/documentService';
import { DogDocument } from '@/types/dog';

interface DocumentUploaderProps {
  dogId: string;
  userId: string;
  existingDocument?: DogDocument | null;
  onUploadComplete?: (document: DogDocument) => void;
}

/**
 * 血統書アップロードコンポーネント
 * @description 1個体につき1つの血統書のみ管理（上書き）
 */
export const DocumentUploader: FC<DocumentUploaderProps> = ({
  dogId,
  userId,
  existingDocument,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  /**
   * ファイルバリデーション
   */
  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return '対応していないファイル形式です。PDF、JPEG、PNGのみアップロード可能です。';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'ファイルサイズが大きすぎます。10MB以下のファイルを選択してください。';
    }

    return null;
  };

  /**
   * ファイル選択処理
   */
  const handleFileSelect = (file: File | null) => {
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setSelectedFile(file);
    setUploadError(null);

    // 既存の血統書がある場合は置き換え確認ダイアログを表示
    if (existingDocument) {
      setShowReplaceDialog(true);
    }
  };

  /**
   * inputからのファイル選択
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  /**
   * ドラッグオーバー
   */
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  /**
   * ドラッグリーブ
   */
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  /**
   * ドロップ
   */
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0] || null;
    handleFileSelect(file);
  };

  /**
   * アップロード実行
   */
  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedDocument = await documentService.uploadDocumentWithMetadata(
        selectedFile,
        dogId,
        userId
      );

      setSelectedFile(null);
      setShowReplaceDialog(false);

      // コールバック実行
      if (onUploadComplete) {
        onUploadComplete(uploadedDocument);
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      setUploadError(error instanceof Error ? error.message : 'アップロードに失敗しました');
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * 置き換えキャンセル
   */
  const handleCancelReplace = () => {
    setSelectedFile(null);
    setShowReplaceDialog(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * ファイル選択ボタンクリック
   */
  const handleClickSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <VStack gap={4} align="stretch">
      {/* ドラッグ&ドロップエリア */}
      <Box
        p={8}
        borderWidth={2}
        borderStyle="dashed"
        borderColor={isDragOver ? 'blue.500' : 'gray.300'}
        borderRadius="lg"
        bg={isDragOver ? 'blue.50' : 'gray.50'}
        textAlign="center"
        cursor="pointer"
        transition="all 0.2s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClickSelect}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        <VStack gap={2}>
          <Text fontSize="lg" fontWeight="medium" color="gray.700">
            📄 ここにファイルをドロップ
          </Text>
          <Text fontSize="sm" color="gray.600">
            または
          </Text>
          <Button colorPalette="blue" size="sm">
            ファイルを選択
          </Button>
          <Text fontSize="xs" color="gray.500" mt={2}>
            対応形式: PDF, JPEG, PNG（最大10MB）
          </Text>
          {existingDocument && (
            <Text fontSize="xs" color="orange.600" mt={1}>
              ※ 既存の血統書は置き換えられます
            </Text>
          )}
        </VStack>
      </Box>

      {/* 選択したファイル情報 */}
      {selectedFile && !showReplaceDialog && (
        <Box p={4} bg="blue.50" borderRadius="md" borderWidth={1} borderColor="blue.200">
          <VStack align="stretch" gap={2}>
            <Text fontSize="sm" fontWeight="medium" color="blue.800">
              選択中: {selectedFile.name}
            </Text>
            <Text fontSize="xs" color="blue.600">
              サイズ: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </Text>
            <HStack gap={2} mt={2}>
              <Button
                colorPalette="blue"
                size="sm"
                onClick={handleUpload}
                disabled={isUploading}
                loading={isUploading}
                flex={1}
              >
                {isUploading ? 'アップロード中...' : 'アップロード'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedFile(null)} flex={1}>
                キャンセル
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {/* エラー表示 */}
      {uploadError && (
        <Box p={3} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200">
          <Text fontSize="sm" color="red.700">
            {uploadError}
          </Text>
        </Box>
      )}

      {/* 置き換え確認ダイアログ */}
      <DialogRoot open={showReplaceDialog} onOpenChange={() => handleCancelReplace()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>血統書の置き換え</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={3}>
              <Text>既存の血統書を新しいファイルで置き換えますか？</Text>
              {selectedFile && (
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    新しいファイル:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {selectedFile.name}
                  </Text>
                </Box>
              )}
              <Text fontSize="sm" color="orange.600">
                ⚠️ この操作は元に戻せません。既存の血統書は削除されます。
              </Text>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <HStack gap={2}>
              <DialogActionTrigger asChild>
                <Button variant="outline" disabled={isUploading} onClick={handleCancelReplace}>
                  キャンセル
                </Button>
              </DialogActionTrigger>
              <Button
                colorPalette="blue"
                onClick={handleUpload}
                loading={isUploading}
                disabled={isUploading}
              >
                {isUploading ? 'アップロード中...' : '置き換える'}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </VStack>
  );
};
